import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { CalendarIcon } from "@shopify/polaris-icons";
import { useTranslation } from "react-i18next";
import {
  DatePicker as ShopifyDatePicker,
  BlockStack,
  Popover,
  TextField,
  Icon,
  Card,
} from "@shopify/polaris";
import type { Range, IconSource } from "@shopify/polaris";
import useIsPhone from "features/files/use-is-phone";

export type DateRangeStrings = {
  start: string;
  end: string;
};

type DateRangePickerProps = {
  /** YYYY-MM-DD */
  start: string;
  /** YYYY-MM-DD */
  end: string;
  onChange: (range: DateRangeStrings) => void;
  label?: string;
  isRequired?: boolean;
  readOnly?: boolean;
  fullWidth?: boolean;
  disableDatesBefore?: Date;
  disableDatesAfter?: Date;
  /** Two months side by side (default false — same as single date picker) */
  multiMonth?: boolean;
};

/** Format up to 8 digits as YYYY-MM-DD (same rules as single date-picker.jsx) */
function formatYmdDigits(chunk: string): string {
  const clean = chunk.replace(/\D/g, "").slice(0, 8);
  let year = clean.slice(0, 4);
  let month = clean.slice(4, 6);
  let day = clean.slice(6, 8);

  if (month && parseInt(month, 10) > 12) {
    month = "12";
  }
  if (day && year && month) {
    const maxDaysInMonth = new Date(
      parseInt(year, 10),
      parseInt(month, 10),
      0
    ).getDate();
    if (parseInt(day, 10) > maxDaysInMonth) {
      day = maxDaysInMonth.toString();
    }
  }

  let formatted = year;
  if (month) {
    formatted += `-${month}`;
  }
  if (day) {
    formatted += `-${day}`;
  }
  return formatted;
}

function parseTypingToDisplay(raw: string): {
  display: string;
  formattedStart: string;
  formattedEnd: string;
  digitCount: number;
} {
  const clean = raw.replace(/\D/g, "").slice(0, 16);
  const formattedStart = formatYmdDigits(clean.slice(0, 8));
  const formattedEnd = formatYmdDigits(clean.slice(8, 16));

  let display: string;
  if (clean.length === 0) {
    display = "-";
  } else if (clean.length <= 8) {
    display = formattedStart;
  } else {
    display = `${formattedStart} – ${formattedEnd}`;
  }

  return { display, formattedStart, formattedEnd, digitCount: clean.length };
}

function ymdToUtcMidnight(ymd: string): Date | null {
  const parts = ymd.split("-").map(Number);
  if (parts.length !== 3) return null;
  const [y, m, d] = parts;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function parseYmdToLocal(ymd: string): Date | undefined {
  if (!ymd || ymd.length < 10) return undefined;
  const [y, m, d] = ymd.split("-").map((x) => parseInt(x, 10));
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return undefined;
  return new Date(y, m - 1, d);
}

function localDateToYmd(date: Date): string {
  const y = date.getFullYear();
  const mo = date.getMonth() + 1;
  const da = date.getDate();
  return `${y}-${String(mo).padStart(2, "0")}-${String(da).padStart(2, "0")}`;
}

function rangeFromProps(start: string, end: string): Range | undefined {
  const s = parseYmdToLocal(start);
  const e = parseYmdToLocal(end);
  if (s && e) {
    return { start: s, end: e };
  }
  if (s) {
    return { start: s, end: s };
  }
  return undefined;
}

function normalizeDisableBoundary(d: Date | undefined): Date | null {
  if (!d) return null;
  return new Date(
    Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
  );
}

/**
 * Range date filter: same shell as {@link ./date-picker.jsx} (one month, LT via AppProvider).
 * Type two dates as digits (YYYYMMDD…) like the single picker, or pick a range on the calendar.
 */
export default function DateRangePicker({
  start,
  end,
  onChange,
  label,
  isRequired = false,
  readOnly = false,
  fullWidth = false,
  disableDatesBefore,
  disableDatesAfter,
  multiMonth = false,
}: DateRangePickerProps) {
  const { t } = useTranslation();
  // The calendar can only move month by month, so a range years away is typed (on a phone the
  // numeric keypad opens). The keypad covers what is under the field, so there the calendar opens above it.
  const isPhone = useIsPhone();
  const [visible, setVisible] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);

  const resolvedLabel = label ?? t("date_range");

  const selectedPolaris: Range | undefined = useMemo(
    () => rangeFromProps(start, end),
    [start, end]
  );

  const initialMonthYear = useMemo(() => {
    const s = parseYmdToLocal(start);
    const e = parseYmdToLocal(end);
    const base = s || e || new Date();
    return { month: base.getMonth(), year: base.getFullYear() };
  }, [start, end]);

  const [{ month, year }, setMonthYear] = useState(initialMonthYear);

  const [inputValue, setInputValue] = useState(() => {
    if (start && end) return `${start} – ${end}`;
    if (start) return `${start} – …`;
    return "-";
  });

  useEffect(() => {
    if (start && end) {
      setInputValue(`${start} – ${end}`);
    } else if (start) {
      setInputValue(`${start} – …`);
    } else {
      setInputValue("-");
    }
  }, [start, end]);

  useEffect(() => {
    setMonthYear(initialMonthYear);
  }, [initialMonthYear, visible]);

  useEffect(() => {
    const handleClickOutside = (event: PointerEvent) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node)
      ) {
        setVisible(false);
      }
    };
    // pointerdown, not mousedown: a tap on a non-clickable area of an iPhone sends no mouse events.
    document.addEventListener("pointerdown", handleClickOutside);
    return () => document.removeEventListener("pointerdown", handleClickOutside);
  }, []);

  const [error, setError] = useState("");
  useEffect(() => {
    if (isRequired && (!start || !end)) {
      setError(t("field_is_required"));
    } else {
      setError("");
    }
  }, [isRequired, start, end, t]);

  const handleMonthChange = useCallback((m: number, y: number) => {
    setMonthYear({ month: m, year: y });
  }, []);

  const applyRangeStrings = useCallback(
    (a: string, b: string) => {
      const ordered = a <= b ? { start: a, end: b } : { start: b, end: a };
      const before = normalizeDisableBoundary(disableDatesBefore);
      const after = normalizeDisableBoundary(disableDatesAfter);
      const startUtc = ymdToUtcMidnight(ordered.start);
      const endUtc = ymdToUtcMidnight(ordered.end);
      if (!startUtc || !endUtc) return;
      if (before && (startUtc < before || endUtc < before)) return;
      if (after && (startUtc > after || endUtc > after)) return;
      onChange(ordered);
      setInputValue(`${ordered.start} – ${ordered.end}`);
    },
    [disableDatesBefore, disableDatesAfter, onChange]
  );

  const handleCalendarChange = useCallback(
    (range: Range) => {
      if (!range.start || !range.end) return;
      const a = localDateToYmd(range.start);
      const b = localDateToYmd(range.end);
      applyRangeStrings(a, b);
    },
    [applyRangeStrings]
  );

  function handleInputValueChange(value: string) {
    if (value === "") {
      setInputValue("-");
      onChange({ start: "", end: "" });
      return;
    }

    const { display, formattedStart, formattedEnd } = parseTypingToDisplay(value);
    setInputValue(display);

    if (formattedStart.length === 10 && formattedEnd.length === 10) {
      applyRangeStrings(formattedStart, formattedEnd);
    }
  }

  const handleOnClose = useCallback(() => {
    setVisible(false);
  }, []);

  return (
    <BlockStack gap="0">
      <div className="sm:min-w-[275px]">
        <Popover
          active={visible}
          autofocusTarget="none"
          preferredAlignment="left"
          fullWidth
          preferInputActivator={false}
          preferredPosition={isPhone ? "above" : "below"}
          preventCloseOnChildOverlayClick
          onClose={handleOnClose}
          activator={
            <div style={fullWidth ? {} : { maxWidth: "380px" }}>
              <TextField
                role="combobox"
                label={resolvedLabel}
                prefix={<Icon source={CalendarIcon as IconSource} />}
                value={inputValue}
                onFocus={() => setVisible(true)}
                onChange={handleInputValueChange}
                autoComplete="off"
                placeholder={t("date_range_placeholder")}
                inputMode="numeric"
                // On a phone, one tap empties an optional range instead of many backspaces.
                clearButton={isPhone && !isRequired && !readOnly && Boolean(start || end)}
                onClearButtonClick={() => handleInputValueChange("")}
                requiredIndicator={isRequired}
                error={error}
                readOnly={readOnly}
              />
            </div>
          }
        >
          <div ref={datePickerRef}>
            {!readOnly && (
              <Card>
                <ShopifyDatePicker
                  month={month}
                  year={year}
                  selected={selectedPolaris}
                  onMonthChange={handleMonthChange}
                  onChange={handleCalendarChange}
                  weekStartsOn={1}
                  allowRange
                  multiMonth={multiMonth}
                  disableDatesBefore={disableDatesBefore}
                  disableDatesAfter={disableDatesAfter}
                />
              </Card>
            )}
          </div>
        </Popover>
      </div>
    </BlockStack>
  );
}
